import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, makeStyles, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components';
import { ChevronLeftRegular, ChevronRightRegular, CalendarRegular, ClockRegular, WarningRegular } from '@fluentui/react-icons';
import { Calendar, DayOfWeek } from '@fluentui/react-calendar-compat';
import { getISOWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAllPeriods } from '../../../hooks/queries/useAllPeriods';
import { usePeriodPickerTranslations } from '../../../hooks/usePriodPickerTranslations';
import { getPeriodWithDate } from '../../../Approver/getPeriodWithDate';
import { useInitialData } from '../../../hooks/useInitialData';
import { usePeriodData } from '../state';

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    centerGroup: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '4px',
    },
    leftGroup: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '4px',
    },
    periodButton: {
        minWidth: '200px',
    },
    calendarContainer: {
        paddingTop: '0',
        paddingBottom: '20px',
        paddingLeft: '24px',
        paddingRight: '24px',
    },
    openPeriodsSurface: {
        maxHeight: '70vh',
        overflowY: 'auto',
    },
    openPeriodsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px',
        minWidth: '220px',
    },
    openPeriodItem: {
        justifyContent: 'flex-start',
        fontWeight: 600,
    },
    openPeriodsTitle: {
        fontWeight: 600,
        fontSize: '12px',
        paddingLeft: '8px',
        paddingBottom: '4px',
    },
    warningPopover: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        fontSize: '16px',
        fontWeight: 500,
    },
    warningIcon: {
        color: tokens.colorPaletteYellowForeground2,
        fontSize: '24px',
        flexShrink: 0,
    },
});

const useTranslatePeriodName = () => {
    const { t } = useTranslation();
    const getPeriodName = useCallback(
        (startDate: string, endDate: string) => {
            const periodStart = new Date(startDate);
            const periodEnd = new Date(endDate);
            const startMonthName = t(`common:shortMonths.${periodStart.getMonth()}`);
            const endMonthName = t(`common:shortMonths.${periodEnd.getMonth()}`);
            const weekNumber = getISOWeek(periodStart);
            return t('common:previewPeriodName', {
                startDate: periodStart.getDate(),
                startMonth: startMonthName,
                endDate: periodEnd.getDate(),
                endMonth: endMonthName,
                week: weekNumber,
            });
        },
        [t],
    );
    return getPeriodName;
};

export const PeriodPicker = memo(function PeriodPicker() {
    const styles = useStyles();
    const { t } = useTranslation();
    const [, setSearchParams] = useSearchParams();
    const { data: allPeriods } = useAllPeriods();
    const {
        data: {
            openPeriods,
            configuration: { autoCreationDisabled },
        },
    } = useInitialData();
    const autoCreationEnabled = !autoCreationDisabled;

    const periodData = usePeriodData();
    const calendarStrings = usePeriodPickerTranslations();
    const getPeriodName = useTranslatePeriodName();
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [calendarValue, setCalendarValue] = useState(() => new Date());
    const [openPeriodsOpen, setOpenPeriodsOpen] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const openPeriodsButtonRef = useRef<HTMLButtonElement>(null);
    const warningTimerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        if (!openPeriods?.length) return;
        setShowWarning(true);
        warningTimerRef.current = setTimeout(() => setShowWarning(false), 5000);
        return () => clearTimeout(warningTimerRef.current);
    }, [openPeriods?.length]);

    const currentPeriodIndex = useMemo(() => {
        if (!allPeriods || !periodData) return -1;
        return allPeriods.findIndex(period => period.startDate?.slice(0, 10) === periodData.startDate.slice(0, 10));
    }, [allPeriods, periodData]);

    const periodName = useMemo(() => {
        if (!periodData) return '';
        return getPeriodName(periodData.startDate, periodData.endDate);
    }, [getPeriodName, periodData]);

    const disablePrev = !autoCreationEnabled && (!allPeriods || currentPeriodIndex <= 0);
    const disableNext = !autoCreationEnabled && (!allPeriods || currentPeriodIndex === -1 || currentPeriodIndex >= allPeriods.length - 1);

    const calendarMinDate = useMemo(() => {
        if (autoCreationEnabled || !allPeriods?.length) return undefined;
        return new Date(allPeriods[0].startDate);
    }, [autoCreationEnabled, allPeriods]);

    const calendarMaxDate = useMemo(() => {
        if (autoCreationEnabled || !allPeriods?.length) return undefined;
        return new Date(allPeriods.at(-1).endDate);
    }, [autoCreationEnabled, allPeriods]);

    const navigateToPeriod = useCallback(
        (startDate: string) => {
            setSearchParams({ date: startDate.slice(0, 10) });
        },
        [setSearchParams],
    );

    const handlePrevPeriod = useCallback(() => {
        if (!allPeriods) return;
        if (currentPeriodIndex > 0) {
            const prevPeriod = allPeriods[currentPeriodIndex - 1];
            navigateToPeriod(prevPeriod.startDate);
            setCalendarValue(new Date(prevPeriod.startDate));
        } else if (autoCreationEnabled && periodData) {
            const prevDate = new Date(periodData.startDate);
            prevDate.setDate(prevDate.getDate() - 1);
            navigateToPeriod(prevDate.toISOString());
            setCalendarValue(prevDate);
        }
    }, [allPeriods, currentPeriodIndex, autoCreationEnabled, periodData, navigateToPeriod]);

    const handleNextPeriod = useCallback(() => {
        if (!allPeriods) return;
        if (currentPeriodIndex !== -1 && currentPeriodIndex < allPeriods.length - 1) {
            const nextPeriod = allPeriods[currentPeriodIndex + 1];
            navigateToPeriod(nextPeriod.startDate);
            setCalendarValue(new Date(nextPeriod.startDate));
        } else if (autoCreationEnabled && periodData) {
            const nextDate = new Date(periodData.endDate);
            nextDate.setDate(nextDate.getDate() + 1);
            navigateToPeriod(nextDate.toISOString());
            setCalendarValue(nextDate);
        }
    }, [allPeriods, currentPeriodIndex, autoCreationEnabled, periodData, navigateToPeriod]);

    const handleCalendarDateChange = useCallback(
        (date: Date) => {
            if (!allPeriods) return;
            const period = getPeriodWithDate(allPeriods, date);
            if (period) {
                navigateToPeriod(period.startDate);
                setCalendarValue(date);
                setCalendarOpen(false);
            } else if (autoCreationEnabled) {
                navigateToPeriod(date.toISOString());
                setCalendarValue(date);
                setCalendarOpen(false);
            }
        },
        [allPeriods, autoCreationEnabled, navigateToPeriod],
    );

    const todayHasPeriod = useMemo(() => {
        if (autoCreationEnabled) return true;
        if (!allPeriods) return false;
        return !!getPeriodWithDate(allPeriods, new Date());
    }, [allPeriods, autoCreationEnabled]);

    const handleGoToToday = useCallback(() => {
        handleCalendarDateChange(new Date());
    }, [handleCalendarDateChange]);

    const handleOpenChange = useCallback((_e: unknown, data: { open: boolean }) => {
        setCalendarOpen(data.open);
    }, []);

    const handleOpenPeriodsOpenChange = useCallback((_e: unknown, data: { open: boolean }) => {
        setOpenPeriodsOpen(data.open);
        if (data.open) {
            setShowWarning(false);
            clearTimeout(warningTimerRef.current);
        }
    }, []);

    const handleOpenPeriodSelect = useCallback(
        (startDate: string) => {
            navigateToPeriod(startDate);
            setCalendarValue(new Date(startDate));
            setOpenPeriodsOpen(false);
        },
        [navigateToPeriod],
    );

    const hasOpenPeriods = !!openPeriods?.length;

    return (
        <div className={styles.container}>
            <div className={styles.leftGroup}>
                {hasOpenPeriods && (
                    <>
                        <Popover open={openPeriodsOpen} onOpenChange={handleOpenPeriodsOpenChange}>
                            <PopoverTrigger disableButtonEnhancement>
                                <Button
                                    ref={openPeriodsButtonRef}
                                    appearance="subtle"
                                    size="small"
                                    icon={<ClockRegular />}
                                    aria-label={t('common:openPeriods')}
                                />
                            </PopoverTrigger>
                            <PopoverSurface className={styles.openPeriodsSurface}>
                                <div className={styles.openPeriodsList}>
                                    <span className={styles.openPeriodsTitle}>{t('common:openPeriods')}</span>
                                    {openPeriods.map(period => (
                                        <Button
                                            key={period.periodId}
                                            appearance={periodData?.id === period.periodId ? 'primary' : 'subtle'}
                                            size="small"
                                            className={styles.openPeriodItem}
                                            onClick={() => handleOpenPeriodSelect(period.startDate)}
                                        >
                                            {getPeriodName(period.startDate, period.endDate)}
                                        </Button>
                                    ))}
                                </div>
                            </PopoverSurface>
                        </Popover>
                        <Popover
                            open={showWarning && !openPeriodsOpen}
                            withArrow
                            positioning={{ position: 'below', target: openPeriodsButtonRef.current ?? undefined }}
                            onOpenChange={() => setShowWarning(false)}
                        >
                            <PopoverSurface onClick={() => setShowWarning(false)}>
                                <div className={styles.warningPopover}>
                                    <WarningRegular className={styles.warningIcon} />
                                    <span>{t('common:openPeriodsWarning', { count: openPeriods.length })}</span>
                                </div>
                            </PopoverSurface>
                        </Popover>
                    </>
                )}
            </div>
            <div className={styles.centerGroup}>
                <Button appearance="subtle" size="small" icon={<ChevronLeftRegular />} onClick={handlePrevPeriod} disabled={disablePrev} />
                <Popover open={calendarOpen} onOpenChange={handleOpenChange} trapFocus>
                    <PopoverTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="medium" icon={<CalendarRegular />} className={styles.periodButton}>
                            {periodName}
                        </Button>
                    </PopoverTrigger>
                    <PopoverSurface>
                        <div className={styles.calendarContainer}>
                            <Calendar
                                firstDayOfWeek={DayOfWeek.Monday}
                                value={calendarValue}
                                onSelectDate={handleCalendarDateChange}
                                strings={calendarStrings}
                                showWeekNumbers
                                showGoToToday={false}
                                minDate={calendarMinDate}
                                maxDate={calendarMaxDate}
                            />
                            <Button appearance="transparent" size="small" onClick={handleGoToToday} disabled={!todayHasPeriod}>
                                {calendarStrings.goToToday}
                            </Button>
                        </div>
                    </PopoverSurface>
                </Popover>
                <Button appearance="subtle" size="small" icon={<ChevronRightRegular />} onClick={handleNextPeriod} disabled={disableNext} />
            </div>
            <div />
        </div>
    );
});
